import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Flex,
  Button,
  HStack,
  VStack,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useBreakpointValue,
  useColorModeValue,
  Circle,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { getTenantInfo, getUnreadItemTypes, getUnreadItemTypesByUserId } from '../services/api';

export default function CommonHeader({ unreadItemTypes = [] }) {
  const { currentUser, isFunctionEnabled, isAdmin } = useAuth();
  const params = useParams();
  const targetUserId = params.targetUserId || null;
  const [logoUrl, setLogoUrl] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [localUnreadItemTypes, setLocalUnreadItemTypes] = useState(unreadItemTypes);
  const isWide = useBreakpointValue({ base: false, md: true });
  
  const headerBg = useColorModeValue('gray.50', 'gray.50');
  const headerBorder = useColorModeValue('gray.200', 'gray.200');

  // テナント情報とロゴを取得
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const info = await getTenantInfo();
        setTenantInfo(info);
        
        // 外部URLの代わりにプロキシ経由でロゴを取得
        const proxyLogoUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071'}/api/tenant/logo`;
        setLogoUrl(proxyLogoUrl);
      } catch (error) {
        console.error('テナント情報の取得に失敗:', error);
      }
    };

    fetchTenantInfo();
  }, []);

  // 未読データを取得
  useEffect(() => {
    const fetchUnreadData = async () => {
      if (!currentUser || !isFunctionEnabled('chat')) {
        setLocalUnreadItemTypes([]);
        return;
      }

      try {
        if (targetUserId) {
          const unreadData = await getUnreadItemTypesByUserId(targetUserId);
          setLocalUnreadItemTypes(unreadData.item_types);
        } else {
          setLocalUnreadItemTypes(unreadItemTypes);
        }
      } catch (error) {
        console.error('未読データの取得に失敗:', error);
        setLocalUnreadItemTypes([]);
      }
    };

    fetchUnreadData();
  }, [currentUser, targetUserId, isFunctionEnabled, unreadItemTypes]);

  // メニューアイテムの配列を作成（デスクトップ用とモバイル用で分ける）
  const menuButtons = [
    // 会社概要リンク（全ユーザー対象、一番最初に表示）
    tenantInfo?.company_intro_url && (
      <Button
        key="company"
        as="a"
        href={tenantInfo.company_intro_url}
        target="_blank"
        rel="noopener noreferrer"
        size="sm"
        bg="white"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
      >
        会社概要
      </Button>
    ),

    // 一般ユーザー用の設備選択ボタン
    !isAdmin && (
      <Button
        key="equipment"
        as={RouterLink}
        to="/equipment"
        size="sm"
        bg="white"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
      >
        設備選択
      </Button>
    ),
    isAdmin && !targetUserId && (
      <Button
        key="users"
        as={RouterLink}
        to="/admin/users"
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
      >
        お客様一覧
      </Button>
    ),
    isAdmin && !targetUserId && (
      <Button
        key="master"
        as={RouterLink}
        to="/admin/master-equipment"
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
>
        マスタ管理
      </Button>
    ),
    isAdmin && !targetUserId && (
      <Button
        key="admin-user-info"
        as={RouterLink}
        to="/admin/user-info"
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
>
        ユーザー情報
      </Button>
    ),

    // 管理者が特定ユーザーを操作している場合のメニュー
    isAdmin && targetUserId && (
      <Button
        key="equipment-admin"
        as={RouterLink}
        to={`/admin/user/${targetUserId}/equipment`}
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
      >
        設備選択
      </Button>
    ),
    // チャット機能が有効な場合のみ表示（管理者が特定ユーザーを操作している場合、または一般ユーザーの場合）
    isFunctionEnabled('chat') && (targetUserId || !isAdmin) && (
      <Button
        key="chat"
        as={RouterLink}
        to={targetUserId ? `/admin/user/${targetUserId}/chat` : '/chat'}
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        position="relative"
        color="black"
        bg="white"
      >
        ご相談
        {isFunctionEnabled('chat') && localUnreadItemTypes.includes("anything") && (
          <Circle
            size="8px"
            bg="yellow.700"
            position="absolute"
            top="-2px"
            right="-2px"
          />
        )}
      </Button>
    ),
    // 一般ユーザーのみ表示
    !isAdmin && (
      <Button
        key="visit"
        size="sm"
        as={RouterLink}
        to={'/visit-reservation'}
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
>
        ご来店
      </Button>
    ),
    // お気に入り画像（管理者が特定ユーザーを操作している場合、または一般ユーザーの場合）
    (targetUserId || !isAdmin) && (
      <Button
        key="like"
        as={RouterLink}
        to={targetUserId ? `/admin/user/${targetUserId}/like-images` : '/like-images'}
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        position="relative"
        color="black"
        bg="white"
      >
        お気に入り画像
        {localUnreadItemTypes.includes("like_images") && (
          <Circle
            size="8px"
            bg="yellow.700"
            position="absolute"
            top="-2px"
            right="-2px"
          />
        )}
      </Button>
    ),
    // 使い方ガイド（管理者が特定ユーザーを操作している場合、または一般ユーザーの場合）
    (targetUserId || !isAdmin) && (
      <Button
        key="guide"
        as={RouterLink}
        to={targetUserId ? `/admin/user/${targetUserId}/guide` : '/guide'}
        size="sm"
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
      >
        使い方ガイド
      </Button>
    ),
    // 一般ユーザーのみ表示
    !isAdmin && (
      <Button
        key="user"
        size="sm"
        as={RouterLink}
        to={'/user-info'}
        _hover={{ color: 'yellow.700', borderColor: 'yellow.400' }}
        _active={{ color: 'yellow.700' }}
        color="black"
        bg="white"
      >
        ユーザー情報
      </Button>
    ),
  ].filter(Boolean);

  // モバイル用のメニューアイテム配列を作成
  const mobileMenuItems = [
    // 会社概要リンク（全ユーザー対象、一番最初に表示）
    tenantInfo?.company_intro_url && {
      key: "company",
      label: "会社概要",
      href: tenantInfo.company_intro_url,
      isExternal: true
    },

    // 一般ユーザー用の設備選択
    !isAdmin && {
      key: "equipment",
      label: "設備選択",
      to: "/equipment"
    },
    // 管理者用メニュー
    isAdmin && !targetUserId && {
      key: "users",
      label: "お客様一覧",
      to: "/admin/users"
    },
    isAdmin && !targetUserId && {
      key: "master",
      label: "マスタ管理",
      to: "/admin/master-equipment"
    },
    isAdmin && !targetUserId && {
      key: "admin-user-info",
      label: "ユーザー情報",
      to: "/admin/user-info"
    },
    // 管理者が特定ユーザーを操作している場合のメニュー
    isAdmin && targetUserId && {
      key: "equipment-admin",
      label: "設備選択",
      to: `/admin/user/${targetUserId}/equipment`
    },
    // チャット機能が有効な場合のみ表示
    isFunctionEnabled('chat') && (targetUserId || !isAdmin) && {
      key: "chat",
      label: "ご相談",
      to: targetUserId ? `/admin/user/${targetUserId}/chat` : '/chat',
      hasNotification: isFunctionEnabled('chat') && localUnreadItemTypes.includes("anything")
    },
    // 一般ユーザーのみ表示
    !isAdmin && {
      key: "visit",
      label: "ご来店",
      to: "/visit-reservation"
    },
    // お気に入り画像
    (targetUserId || !isAdmin) && {
      key: "like",
      label: "お気に入り画像",
      to: targetUserId ? `/admin/user/${targetUserId}/like-images` : '/like-images',
      hasNotification: localUnreadItemTypes.includes("like_images")
    },
    // 使い方ガイド
    (targetUserId || !isAdmin) && {
      key: "guide",
      label: "使い方ガイド",
      to: targetUserId ? `/admin/user/${targetUserId}/guide` : '/guide'
    },
    // 一般ユーザーのみ表示
    !isAdmin && {
      key: "user",
      label: "ユーザー情報",
      to: "/user-info"
    },
  ].filter(Boolean);

  return (
    <Box as="header" bg={headerBg} borderBottomWidth="1px" borderColor={headerBorder} px={4} py={3} mb={2} w="100%">
      <Flex align="center" justify="space-between">
        <RouterLink to="/">
          <img 
            src={logoUrl} 
            alt="logo" 
            width="300px" 
            height="90px"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            style={{ maxWidth: '100%', height: 'auto', cursor: 'pointer' }}
          />
        </RouterLink>
        {isWide ? (
          <HStack spacing={1}>
            {menuButtons}
          </HStack>
        ) : (
          // ドロップダウンメニュー
          <Menu>
            <MenuButton 
              as={IconButton} 
              icon={
                <VStack spacing={0}>
                  <HamburgerIcon />
                  <Text fontSize="xs" fontWeight="bold">MENU</Text>
                </VStack>
              } 
              variant="outline" 
            />
            <MenuList>
              {mobileMenuItems.map((item) => (
                <MenuItem
                  key={item.key}
                  as={item.isExternal ? "a" : RouterLink}
                  to={item.to}
                  href={item.href}
                  target={item.isExternal ? "_blank" : undefined}
                  rel={item.isExternal ? "noopener noreferrer" : undefined}
                  _hover={{ bg: 'gray.100', color: 'yellow.700' }}
                  _active={{ bg: 'gray.200', color: 'yellow.700' }}
                  position="relative"
                  w="100%"
                  justifyContent="flex-start"
                >
                  {item.label}
                  {item.hasNotification && (
                    <Circle
                      size="8px"
                      bg="yellow.700"
                      position="absolute"
                      top="8px"
                      right="8px"
                    />
                  )}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </Flex>
    </Box>
  );
} 